import './CallToActionButton.scss';

import { registryComponent } from '@boost-sd/components-registry/registry';
import Button from '@components/Button';
import { Icon } from '@components/Icon';
import useTranslation from '@hooks/useTranslation';
import { useValidateBreakpoint } from '@providers/AppProvider/Breakpoint/hooks';
import classnames, { createClsNameMap } from '@utils/classnames';

export type CTAButtonProps = {
  icon?: string;
  iconPosition?: 'left' | 'right';
  text?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  backgroundColor?: string;
  backgroundColorOnHover?: string;
  borderColor?: string;
  borderColorOnHover?: string;
  textColor?: string;
  textColorOnHover?: string;
  fontFamily?: string;
  fontStyle?: string;
  fontSize?: number;
  fontWeight?: string | number;
  textAlignment?: 'center' | 'end' | 'justify' | 'left' | 'match-parent' | 'right' | 'start';
  shape?: 'rectangle' | 'round' | 'circle' | 'square';
  fullWidth?: boolean;
  width?: string;
  height?: string;
  position?: 'left' | 'center' | 'right';
  showButtonOnHover?: boolean;
  iconImageSrc?: string;
  hideOnMobile?: boolean;
  disabled?: boolean;
  textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase';
  className?: string;
};

export const clsNameMap = createClsNameMap({
  elements: {
    text: createClsNameMap(),
  },
})('cta-button');

const CTAButton = ({
  icon,
  text,
  shape,
  onClick,
  iconPosition,
  iconImageSrc,
  fullWidth,
  position,
  showButtonOnHover,
  hideOnMobile,
  disabled,
  className,
  ...style
}: CTAButtonProps) => {
  const { t } = useTranslation();

  const mobileBreakpoint = useValidateBreakpoint('mobile');

  const {
    width,
    height,
    fontStyle,
    fontSize,
    fontFamily,
    fontWeight,
    textAlignment,
    textTransform,
    textColor,
    borderColor,
    backgroundColor,
    textColorOnHover,
    borderColorOnHover,
    backgroundColorOnHover,
  } = style;

  const buttonText = text && t(text);

  const defaultStyle = {
    width,
    height: height ? `${height}px` : 'fit-content',
    fontStyle,
    fontSize,
    fontFamily,
    textAlign: textAlignment,
    fontWeight,
    textTransform: textTransform || undefined,
    color: textColor,
    borderColor,
    background: backgroundColor,
    minWidth: 0,
  };

  const handleHoverButton = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (textColorOnHover) e.currentTarget.style.color = textColorOnHover;
    if (borderColorOnHover) e.currentTarget.style.borderColor = borderColorOnHover;
    if (backgroundColorOnHover) e.currentTarget.style.backgroundColor = backgroundColorOnHover;
  };

  const handleBlurButton = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (textColor) e.currentTarget.style.color = textColor;
    if (borderColor) e.currentTarget.style.borderColor = borderColor;
    if (backgroundColor) e.currentTarget.style.backgroundColor = backgroundColor;
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (onClick) onClick(e);
  };

  const buildClassFromTextTranslate = () => {
    let className = '';

    switch (buttonText) {
      case t('productItem.atcAvailableLabel'):
        className = classnames('btn-add-to-cart');
        break;
    }

    return className;
  };

  if (hideOnMobile && mobileBreakpoint) return null;

  return (
    <Button
      className={className || buildClassFromTextTranslate()}
      onMouseEnter={handleHoverButton}
      onMouseLeave={handleBlurButton}
      style={defaultStyle}
      modifiers={{
        round: shape === 'round',
        circle: shape === 'circle',
        square: shape === 'square',
        border: true,
        left: position === 'left',
        center: position === 'center',
        right: position === 'right',
        'full-width': !!fullWidth || width === '100%',
        'show-on-hover': showButtonOnHover,
        'half-width': width === '50%',
      }}
      aria-label={buttonText}
      onClick={handleClick}
      disabled={disabled}
    >
      {(icon || iconImageSrc) && (
        <Icon
          innerSvg={icon}
          src={iconImageSrc}
          modifiers={{ 'fill-current-color': true, 'on-right': iconPosition === 'right' }}
        />
      )}
      <span className={clsNameMap.elm('text')}>
        {textTransform === 'none' ? buttonText : buttonText?.toLowerCase()}
      </span>
    </Button>
  );
};

export default registryComponent('CTAButton', CTAButton);
